/**
 * Print helper for master data lists (Customers, Vendors, Chart of Accounts).
 * Builds an HTML document and opens the browser print dialog.
 */
import { addPrintBrand } from './printBranding';

const esc = (s) => String(s ?? '').replace(/[<>&"']/g, (c) => ({
  '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;'
}[c]));

const buildHeader = (activeCompany, title, meta) => `
  <div class="header">
    <div class="company-block">
      ${activeCompany?.brandLogoUrl ? `<img class="logo" src="${esc(activeCompany.brandLogoUrl)}" alt="logo" />` : ''}
      <div>
        <div class="company-name">${esc(activeCompany?.companyName || '')}</div>
        ${activeCompany?.address ? `<div class="company-detail">${esc(activeCompany.address)}</div>` : ''}
        ${activeCompany?.phone ? `<div class="company-detail">Phone: ${esc(activeCompany.phone)}</div>` : ''}
        ${activeCompany?.email ? `<div class="company-detail">Email: ${esc(activeCompany.email)}</div>` : ''}
        ${activeCompany?.ntn ? `<div class="company-detail">${(activeCompany.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN'}: ${esc(activeCompany.ntn)}</div>` : ''}
      </div>
    </div>
    <div class="title-block">
      <div class="doc-title">${esc(title)}</div>
      <div class="meta">${esc(meta || `Printed: ${new Date().toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' })}`)}</div>
    </div>
  </div>
`;

const baseCSS = (accent = '#800020') => `
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; color: #1A1A1A; margin: 0; padding: 0; font-size: 12px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 3px solid ${accent}; margin-bottom: 14px; }
  .company-block { display: flex; gap: 12px; align-items: flex-start; flex: 1; }
  .logo { max-width: 60px; max-height: 60px; object-fit: contain; }
  .company-name { font-size: 16px; font-weight: 700; color: ${accent}; margin-bottom: 2px; }
  .company-detail { font-size: 10px; color: #555; line-height: 1.4; }
  .title-block { text-align: right; }
  .doc-title { font-size: 16px; font-weight: 700; color: ${accent}; letter-spacing: 1px; }
  .meta { font-size: 10px; color: #666; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: ${accent}; color: white; padding: 6px 8px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; }
  th.right { text-align: right; }
  td { padding: 5px 8px; border-bottom: 1px solid #e0e0e0; vertical-align: top; }
  td.right { text-align: right; font-family: 'Consolas', monospace; }
  tr:nth-child(even) td { background: #fafafa; }
  .summary { margin-top: 14px; padding: 8px 12px; background: #f7f7f7; border-radius: 6px; font-size: 11px; color: #333; display: flex; justify-content: space-between; }
  .footer { margin-top: 18px; padding-top: 8px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #777; }
  @media print { body { margin: 0; } .no-print { display: none; } }
`;

const openPrintWindow = (html) => {
  const w = window.open('', '_blank');
  if (!w) { alert('Pop-up blocked. Please allow pop-ups for this site to print.'); return; }
  w.document.write(html);
  addPrintBrand(w);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
};

// ─── Customers ──────────────────────────────────────────────────────────────
export const printCustomers = (customers, opts = {}) => {
  const { activeCompany, regions = [], areas = [], filterLabel = '' } = opts;
  const regMap = {}; regions.forEach(r => { regMap[r.id] = r.regionName; });
  const areaMap = {}; areas.forEach(a => { areaMap[a.id] = a.areaName; });

  const rows = customers.map((c, i) => `
    <tr>
      <td>${i + 1}</td>
      <td><strong>${esc(c.companyName || '')}</strong>${c.linkedAccountCode ? `<div style="font-size:9px;color:#999">A/C: ${esc(c.linkedAccountCode)}</div>` : ''}</td>
      <td>${esc(c.contactPerson || '-')}</td>
      <td>${esc(c.phone || '-')}</td>
      <td>${esc(c.email || '-')}</td>
      <td>${esc([regMap[c.regionId], areaMap[c.areaId]].filter(Boolean).join(' / ') || '-')}</td>
      <td>${esc(c.ntn || '-')}</td>
      <td class="right">${(Number(c.creditLimit) || 0).toLocaleString('en-PK')}</td>
      <td>${c.isActive === false ? 'Inactive' : 'Active'}</td>
    </tr>
  `).join('');

  const summary = `
    <div class="summary">
      <span>Total Customers: <strong>${customers.length}</strong></span>
      <span>Active: <strong>${customers.filter(c => c.isActive !== false).length}</strong> · Inactive: <strong>${customers.filter(c => c.isActive === false).length}</strong></span>
    </div>
  `;

  const html = `<html><head><title>Customer List</title><style>${baseCSS()}</style></head><body>
    ${buildHeader(activeCompany, 'CUSTOMER LIST', filterLabel ? `${filterLabel} · Printed: ${new Date().toLocaleDateString('en-PK')}` : null)}
    <table>
      <thead><tr>
        <th style="width:30px">#</th><th>Customer</th><th>Contact</th><th>Phone</th><th>Email</th>
        <th>Region / Area</th><th>${(activeCompany?.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN'}</th><th class="right">Credit Limit</th><th>Status</th>
      </tr></thead>
      <tbody>${rows || '<tr><td colspan="9" style="text-align:center;color:#999">No customers</td></tr>'}</tbody>
    </table>
    ${summary}
  </body></html>`;
  openPrintWindow(html);
};

// ─── Vendors ────────────────────────────────────────────────────────────────
export const printVendors = (vendors, opts = {}) => {
  const { activeCompany, filterLabel = '' } = opts;

  const rows = vendors.map((v, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${esc(v.vendorCode || '-')}</td>
      <td><strong>${esc(v.vendorName || '')}</strong>${v.linkedAccountCode ? `<div style="font-size:9px;color:#999">A/C: ${esc(v.linkedAccountCode)}</div>` : ''}</td>
      <td>${esc(v.vendorType || '-')}</td>
      <td>${esc(v.contactPerson || '-')}</td>
      <td>${esc(v.phone || v.mobile || '-')}</td>
      <td>${esc(v.email || '-')}</td>
      <td>${esc(v.city || '-')}</td>
      <td>${esc(v.ntn || '-')}</td>
      <td class="right">${v.whtApplicable ? `${v.whtRate || 0}%` : '-'}</td>
      <td>${v.isActive === false ? 'Inactive' : 'Active'}</td>
    </tr>
  `).join('');

  const summary = `
    <div class="summary">
      <span>Total Vendors: <strong>${vendors.length}</strong></span>
      <span>Active: <strong>${vendors.filter(v => v.isActive !== false).length}</strong> · Inactive: <strong>${vendors.filter(v => v.isActive === false).length}</strong></span>
    </div>
  `;

  const html = `<html><head><title>Vendor List</title><style>${baseCSS()}</style></head><body>
    ${buildHeader(activeCompany, 'VENDOR LIST', filterLabel ? `${filterLabel} · Printed: ${new Date().toLocaleDateString('en-PK')}` : null)}
    <table>
      <thead><tr>
        <th style="width:30px">#</th><th>Code</th><th>Vendor</th><th>Type</th><th>Contact</th>
        <th>Phone</th><th>Email</th><th>City</th><th>${(activeCompany?.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN'}</th><th class="right">WHT</th><th>Status</th>
      </tr></thead>
      <tbody>${rows || '<tr><td colspan="11" style="text-align:center;color:#999">No vendors</td></tr>'}</tbody>
    </table>
    ${summary}
  </body></html>`;
  openPrintWindow(html);
};

// ─── Chart of Accounts (hierarchical tree) ──────────────────────────────────
export const printChartOfAccounts = (accounts, opts = {}) => {
  const { activeCompany, filterLabel = '' } = opts;

  // Build parent → children map and find roots (no parent)
  const byParent = {};
  accounts.forEach(a => {
    const pid = a.parentId || '__ROOT__';
    if (!byParent[pid]) byParent[pid] = [];
    byParent[pid].push(a);
  });
  Object.values(byParent).forEach(arr => arr.sort((a, b) => (a.code || '').localeCompare(b.code || '')));

  const typeColor = (t) => {
    const x = (t || '').toLowerCase();
    if (x === 'asset') return '#1565C0';
    if (x === 'liability') return '#D84315';
    if (x === 'equity') return '#6A1B9A';
    if (x === 'revenue' || x === 'income') return '#2E7D32';
    if (x === 'expense') return '#C62828';
    return '#555';
  };

  const renderRow = (acc, depth) => {
    const indent = depth * 18;
    const isGroup = acc.isGroup === true || acc.level < 5;
    const tColor = typeColor(acc.accountType);
    return `
      <tr>
        <td style="padding-left:${8 + indent}px">
          <span style="font-family:Consolas,monospace;font-size:10px;color:#666">${esc(acc.code || '')}</span>
        </td>
        <td style="padding-left:${8 + indent}px">
          <span style="font-weight:${isGroup ? 700 : 500}">${esc(acc.name || '')}</span>
          ${acc.linkedEntityType ? `<span style="font-size:9px;color:#888;margin-left:6px">↳ ${esc(acc.linkedEntityType)}</span>` : ''}
        </td>
        <td><span style="font-size:10px;color:${tColor};text-transform:capitalize">${esc(acc.accountType || '')}</span></td>
        <td style="text-align:center;font-size:10px;color:#888">${acc.level || '-'}</td>
        <td style="text-align:center;font-size:10px">${isGroup ? '<span style="color:#888">Group</span>' : '<span style="color:#1565C0">Postable</span>'}</td>
        <td style="font-size:10px;color:${acc.isActive === false ? '#C62828' : '#2E7D32'}">${acc.isActive === false ? 'Inactive' : 'Active'}</td>
      </tr>
    `;
  };

  const renderTree = (parentId, depth) => {
    const children = byParent[parentId] || [];
    return children.map(c => renderRow(c, depth) + renderTree(c.id, depth + 1)).join('');
  };

  const body = renderTree('__ROOT__', 0);

  // Type counts
  const counts = {};
  accounts.forEach(a => { const t = a.accountType || 'other'; counts[t] = (counts[t] || 0) + 1; });
  const countsHtml = Object.entries(counts).map(([t, n]) => `<span style="text-transform:capitalize">${esc(t)}: <strong>${n}</strong></span>`).join(' · ');

  const summary = `
    <div class="summary">
      <span>Total Accounts: <strong>${accounts.length}</strong></span>
      <span>${countsHtml}</span>
    </div>
  `;

  const html = `<html><head><title>Chart of Accounts</title><style>${baseCSS()}
    /* COA-specific tweaks */
    table td:first-child { white-space: nowrap; }
  </style></head><body>
    ${buildHeader(activeCompany, 'CHART OF ACCOUNTS', filterLabel ? `${filterLabel} · Printed: ${new Date().toLocaleDateString('en-PK')}` : null)}
    <table>
      <thead><tr>
        <th>Code</th><th>Account Name</th><th>Type</th><th style="text-align:center">Level</th><th style="text-align:center">Kind</th><th>Status</th>
      </tr></thead>
      <tbody>${body || '<tr><td colspan="6" style="text-align:center;color:#999">No accounts</td></tr>'}</tbody>
    </table>
    ${summary}
  </body></html>`;
  openPrintWindow(html);
};
